
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { signInWithEmail, signUpWithEmail } from '../../hooks/useAuth';

interface AuthModalProps {
  onClose: () => void;
}

type Step = 'credentials' | 'username';

export default function AuthModal({ onClose }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setUsername('');
    setError('');
    setStep('credentials');
  };

  const handleCredentialsNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }
    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }
    setError('');
    if (mode === 'signup') {
      setStep('username');
    } else {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const { error: err } = await signInWithEmail(email, password);
      if (err) {
        setError('メールアドレスまたはパスワードが正しくありません');
      } else {
        onClose();
      }
    } catch {
      setError('エラーが発生しました。もう一度お試しください');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) {
      setError('ユーザーネームを入力してください');
      return;
    }
    if (trimmed.length < 3) {
      setError('3文字以上で入力してください');
      return;
    }
    if (trimmed.length > 20) {
      setError('20文字以内で入力してください');
      return;
    }
    if (!/^[a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]+$/.test(trimmed)) {
      setError('英数字・日本語・アンダースコアのみ使用できます');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { error: err } = await signUpWithEmail(email, password, trimmed);
      if (err) {
        const msg = err.message || '';
        if (msg.includes('already registered') || msg.includes('User already registered')) {
          setError('このメールアドレスは既に登録されています');
          setStep('credentials');
        } else if (msg.includes('ユーザーネームは既に')) {
          setError('このユーザーネームは既に使用されています。別の名前をお試しください');
        } else {
          setError('登録に失敗しました。もう一度お試しください');
        }
      } else {
        onClose();
      }
    } catch {
      setError('エラーが発生しました。もう一度お試しください');
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 999999 }}
      onClick={handleBackgroundClick}
    >
      <div
        className="bg-white rounded-2xl max-w-sm w-full p-6 relative shadow-2xl"
        style={{ zIndex: 1000000 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full mx-auto mb-3">
            <i className={`ri-${step === 'username' ? 'user-smile-line' : 'user-line'} text-2xl text-white`}></i>
          </div>
          <h3 className="text-lg font-bold text-gray-800">
            {mode === 'login' ? 'ログイン' : step === 'credentials' ? 'アカウント作成' : 'ユーザーネームを設定'}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {mode === 'login'
              ? '学習習熟度を記録するにはログインが必要です'
              : step === 'credentials'
              ? 'メールアドレスとパスワードを入力してください'
              : 'あなたの表示名を決めてください（後から変更可能）'}
          </p>
        </div>

        {/* Step indicator for signup */}
        {mode === 'signup' && (
          <div className="flex items-center justify-center gap-2 mb-5">
            <div className={`w-2 h-2 rounded-full transition-all ${step === 'credentials' ? 'bg-teal-500 w-6' : 'bg-teal-500'}`}></div>
            <div className={`w-2 h-2 rounded-full transition-all ${step === 'username' ? 'bg-teal-500 w-6' : 'bg-gray-300'}`}></div>
          </div>
        )}

        {/* Tab switch — only on credentials step */}
        {step === 'credentials' && (
          <div className="flex bg-gray-100 rounded-full p-1 mb-5">
            <button
              type="button"
              onClick={() => { setMode('login'); resetForm(); }}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === 'login' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ログイン
            </button>
            <button
              type="button"
              onClick={() => { setMode('signup'); resetForm(); }}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === 'signup' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              新規登録
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-4 py-2.5 mb-4">
            <i className="ri-error-warning-line mr-1"></i>
            {error}
          </div>
        )}

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <form onSubmit={handleCredentialsNext}>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">メールアドレス</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">パスワード</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6文字以上"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              {loading ? '処理中...' : mode === 'login' ? 'ログイン' : '次へ →'}
            </button>
          </form>
        )}

        {/* Step 2: Username (signup only) */}
        {step === 'username' && (
          <form onSubmit={handleSignup}>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-600 mb-1">ユーザーネーム</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="例: nagayama_study"
                maxLength={20}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1.5">
                3〜20文字。英数字・日本語・アンダースコア（_）が使用できます
              </p>
              <div className="text-right text-xs text-gray-400 mt-1">{username.length}/20</div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>アカウントを作成中...
                </span>
              ) : 'アカウントを作成'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('credentials'); setError(''); }}
              className="w-full py-2 text-gray-500 text-xs font-medium hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              ← 戻る
            </button>
          </form>
        )}

        {step === 'credentials' && (
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            キャンセル
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
